// Control Plan ingest seed — Zn circuit rows that land as tag-map entries [syn] (tarp.A8).

import type { MetDiagnosticsSection } from '@/versions/v1/schema/met-diagnostics-cause';
import type { SignalCadence, SignalReliability } from '@/versions/v1/schema/tag-mapping';
import type { SignalRole } from '@/versions/v1/schema/tag-mapping';
import type { Band } from '@/versions/v1/schema/reading';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';

export type ControlPlanSectionId = MetDiagnosticsSection | 'equipment';

export type ControlPlanRow = {
  id: string;
  role: SignalRole;
  instrumentTag: string;
  specOverlay: Band;
  reliability: SignalReliability;
  cadence: SignalCadence;
  reliabilityBasis: string;
  sectionId?: ControlPlanSectionId;
  zoneId?: string;
  fixed?: boolean;
  sourceId: string;
  readingId?: string;
};

const READING_BY_ID = new Map(READINGS.map((row) => [row.id, row]));

function bandFromReadingId(readingId: string): Band {
  const reading = READING_BY_ID.get(readingId);
  if (!reading) return {};
  if (reading.spec?.hi !== undefined && reading.higherIsBetter === false) {
    return { hi: reading.spec.hi };
  }
  if (reading.design !== undefined) {
    return { lo: reading.design };
  }
  if (reading.spec?.lo !== undefined) {
    return { lo: reading.spec.lo };
  }
  if (reading.spec?.hi !== undefined) {
    return { hi: reading.spec.hi };
  }
  return {};
}

const SRC_CONTROL_PLAN = 'src-control-plan-zn';

/**
 * The row's own name as the plant's Control Plan writes it. `role` is an id
 * slug, never word-processed for the glass; a row with no entry here keeps the
 * label its own reading carries.
 */
export const CONTROL_PLAN_ROW_NAMES: Readonly<Record<string, string>> = {
  'cp-bm-brg-temp': 'BM bearing temp',
};

export function controlPlanRowName(rowId: string | null | undefined): string | undefined {
  return rowId ? CONTROL_PLAN_ROW_NAMES[rowId] : undefined;
}

/** Rows from `src-control-plan-zn` — conformance overlays, never triggers. */
export const CONTROL_PLAN_ZN_ROWS: readonly ControlPlanRow[] = [
  {
    id: 'cp-zn-ph',
    role: 'zn-conditioner-ph',
    instrumentTag: 'AT-150',
    specOverlay: { lo: 10.8, hi: 11.2 },
    reliability: 'online-high',
    cadence: 'continuous',
    reliabilityBasis: 'pH probe to DCS, online',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-c-zn-ph',
  },
  {
    id: 'cp-zn-rec',
    role: 'zn-recovery',
    instrumentTag: 'kpi-zn-rec',
    specOverlay: bandFromReadingId('rd-kpi-zn-rec'),
    reliability: 'manual-low',
    cadence: 'derived',
    reliabilityBasis: 'Metal accounting back-calc, shift close',
    sectionId: 'Zn Recovery Below Target',
    zoneId: 'oz-main',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-kpi-zn-rec',
  },
  {
    id: 'cp-zn-con',
    role: 'zn-con-grade',
    instrumentTag: 'SA05',
    specOverlay: bandFromReadingId('rd-kpi-zn-grade'),
    reliability: 'manual-low',
    cadence: 'rush',
    reliabilityBasis: 'Zn concentrate assay, manual grab',
    sectionId: 'Zn Con Grade Below Spec',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-kpi-zn-grade',
  },
  {
    id: 'cp-cuso4',
    role: 'reagent-cuso4-rate',
    instrumentTag: 'CuSO4',
    specOverlay: { lo: 140, hi: 160 },
    reliability: 'online-high',
    cadence: 'continuous',
    reliabilityBasis: 'DCS dosing FIT, online',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-reag-cuso4',
  },
  {
    id: 'cp-zn-rgh-dens',
    role: 'zn-rougher-density',
    instrumentTag: 'DT-502',
    specOverlay: { lo: 27, hi: 33 },
    reliability: 'online-high',
    cadence: 'continuous',
    reliabilityBasis: 'DCS density transmitter, online',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-zn-rgh-dens',
  },
  {
    id: 'cp-pb-rec',
    role: 'pb-recovery',
    instrumentTag: 'kpi-pb-rec',
    specOverlay: bandFromReadingId('rd-kpi-pb-rec'),
    reliability: 'manual-low',
    cadence: 'derived',
    reliabilityBasis: 'Metal accounting back-calc, shift close',
    sectionId: 'Pb Recovery Below Target',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-kpi-pb-rec',
  },
  {
    id: 'cp-pb-con',
    role: 'pb-con-grade',
    instrumentTag: 'kpi-pb-grade',
    specOverlay: bandFromReadingId('rd-kpi-pb-grade'),
    reliability: 'manual-low',
    cadence: 'rush',
    reliabilityBasis: 'Pb concentrate assay, manual grab',
    sectionId: 'Pb Con Grade Below 67% Pb',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-kpi-pb-grade',
  },
  {
    id: 'cp-ag-rec',
    role: 'ag-recovery',
    instrumentTag: 'kpi-ag-rec',
    specOverlay: bandFromReadingId('rd-kpi-ag-rec'),
    reliability: 'manual-low',
    cadence: 'derived',
    reliabilityBasis: 'Metal accounting back-calc, shift close',
    sectionId: 'Ag Recovery Below Target',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-kpi-ag-rec',
  },
  {
    id: 'cp-tails-pb',
    role: 'pb-con-grade',
    instrumentTag: 'SA05',
    specOverlay: bandFromReadingId('rd-spec-tails-pb'),
    reliability: 'manual-low',
    cadence: 'shift',
    reliabilityBasis: 'Tailings assay, shift composite',
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-spec-tails-pb',
  },
  {
    id: 'cp-bm-brg-temp',
    role: 'bm-bearing-temp',
    instrumentTag: 'TE-622-625',
    specOverlay: bandFromReadingId('rd-bm-brg-temp'),
    reliability: 'online-high',
    cadence: 'continuous',
    reliabilityBasis: 'DCS bearing RTD, online',
    sectionId: 'equipment',
    fixed: true,
    sourceId: SRC_CONTROL_PLAN,
    readingId: 'rd-bm-brg-temp',
  },
];
